// src/api.js
import axios from 'axios';

const API_KEY = process.env.REACT_APP_API_KEY || 'EAP-2024-7X9Y-2Z3W-4V5U-6T7S-8R9Q-0P1O';
const API_URL = process.env.REACT_APP_API_URL || 'https://eap.ethernet.edu.et/api';

// Validate that environment variables are set
if (!process.env.REACT_APP_API_KEY) {
  console.warn('REACT_APP_API_KEY is not set in environment variables, using fallback');
}

if (!process.env.REACT_APP_API_URL) {
  console.warn('REACT_APP_API_URL is not set in environment variables, using fallback');
}

/** Hide internal API status text (cache/DB) from public result pages. */
export function shouldDisplayPortalResultMessage(message) {
  if (!message || typeof message !== 'string') {
    return false;
  }
  const normalized = message.trim().toLowerCase();
  if (!normalized || normalized.includes('not released')) {
    return false;
  }
  const internalPatterns = [
    'retrieved from cache',
    'retrieved from database',
    'retrieved successfully',
    'result retrieved',
    'results retrieved',
    'and cached',
    'from cache',
    'from database',
  ];
  return !internalPatterns.some((pattern) => normalized.includes(pattern));
}

// Input sanitization function
const sanitizeInput = (input) => {
  if (typeof input !== 'string') {
    throw new Error('Invalid input type');
  }
  
  // Remove any potential script tags and dangerous characters
  return input
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/[<>]/g, '')
    .trim();
};

// Username validation
const validateUsername = (username) => {
  if (!username || typeof username !== 'string') {
    throw new Error('Username is required and must be a string');
  }
  
  const sanitized = sanitizeInput(username);
  
  if (sanitized.length < 5 || sanitized.length > 15) {
    throw new Error('Username must be between 5 and 15 characters');
  }
  
  // Only allow alphanumeric characters and dots
  if (!/^[a-zA-Z0-9.]+$/.test(sanitized)) {
    throw new Error('Username must contain only letters, numbers, and dots');
  }
  
  return sanitized;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'X-API-Key': API_KEY,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Handle redirects
  maxRedirects: 5,
  validateStatus: function (status) {
    return status >= 200 && status < 500; // Accept all status codes less than 500
  }
});

// Request deduplication
const pendingRequests = new Map();

// Add request interceptor to handle CORS and deduplication
api.interceptors.request.use(
  (config) => {
    // Add CORS headers to request
    config.headers['Access-Control-Allow-Origin'] = '*';
    config.headers['Access-Control-Allow-Methods'] = 'GET, POST, PUT, DELETE, OPTIONS';
    config.headers['Access-Control-Allow-Headers'] = 'Content-Type, X-API-Key';
    
    // Request deduplication for identical requests
    const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.data)}`;
    if (pendingRequests.has(requestKey)) {
      // Cancel duplicate request
      const source = axios.CancelToken.source();
      config.cancelToken = source.token;
      source.cancel('Duplicate request cancelled');
    } else {
      // Store pending request
      pendingRequests.set(requestKey, Date.now());
      
      // Clean up old requests (older than 30 seconds)
      const now = Date.now();
      for (const [key, timestamp] of pendingRequests.entries()) {
        if (now - timestamp > 30000) {
          pendingRequests.delete(key);
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor to handle errors and cleanup
api.interceptors.response.use(
  (response) => {
    // Clean up pending request
    const requestKey = `${response.config.method}-${response.config.url}-${JSON.stringify(response.config.data)}`;
    pendingRequests.delete(requestKey);
    return response;
  },
  (error) => {
    // Clean up pending request on error
    if (error.config) {
      const requestKey = `${error.config.method}-${error.config.url}-${JSON.stringify(error.config.data)}`;
      pendingRequests.delete(requestKey);
    }
    
    if (error.response) {
      // Handle specific error cases
      switch (error.response.status) {
        case 401:
          error.message = 'Invalid API key';
          break;
        case 404:
          error.message = 'Student not found';
          break;
        case 429:
          error.message = 'Too many requests. Please wait a moment and try again.';
          break;
        case 500:
          error.message = 'Server error occurred';
          break;
        default:
          error.message = error.response.data?.message || 'An error occurred';
      }
    } else if (error.request) {
      // Network error
      error.message = 'Network error occurred. Please check your connection.';
    } else if (axios.isCancel(error)) {
      // Cancelled request (duplicate)
      error.message = 'Request cancelled';
    } else {
      // Other errors
      error.message = 'An unexpected error occurred';
    }
    return Promise.reject(error);
  }
);

// Auto-attach token if it's in localStorage
const token = localStorage.getItem('token');
if (token) {
  api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

// Username related API calls
export const usernameApi = {
  getAllUsernames: () => api.get('/usernames'),
  searchUsername: (query) => api.get(`/usernames/search?q=${query}`),
};

// Exit exam related API calls
export const exitExamApi = {
  getResult: async (username) => {
    try {
      // Validate and sanitize username
      const validatedUsername = validateUsername(username);
      
      const response = await axios.post(
        `${API_URL}/exit-exam-result`,
        { username: validatedUsername },
        {
          headers: {
            'X-API-Key': API_KEY,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 10000 // 10 second timeout
        }
      );
      
      // Validate response
      if (!response.data) {
        throw new Error('Invalid response from server');
      }
      
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data.message || 'Failed to fetch result');
      } else if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
      } else if (error.message.includes('Invalid input type') || 
                 error.message.includes('Username is required') ||
                 error.message.includes('Username must be between') ||
                 error.message.includes('Username contains invalid')) {
        throw error; // Re-throw validation errors
      } else {
        throw new Error('Network error occurred');
      }
    }
  }
};

export default api;
